
import React, { useState } from 'react';
import axios from 'axios';
import { Edit, Trash, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import GroupDialog, { Group } from './GroupDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface GroupsTabProps {
  groups: Group[];
  fetchGroups: () => Promise<void>;
}

const GroupsTab: React.FC<GroupsTabProps> = ({
  groups,
  fetchGroups
}) => {
  const [currentGroup, setCurrentGroup] = useState<Group | null>(null);
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleOpenGroupDialog = (group?: Group) => {
    if (group) {
      setCurrentGroup(group);
    } else {
      // Ensure sort_order is set as a number, not optional
      setCurrentGroup({
        id: '',
        name: '',
        description: '',
        sort_order: 0
      });
    }
    setIsGroupDialogOpen(true);
  };

  const handleSaveGroup = async () => {
    try {
      if (!currentGroup) return;
      setIsSubmitting(true);
      
      // Validate the group name
      if (!currentGroup.name.trim()) {
        toast({
          title: "Validation Error",
          description: "Group name is required.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }
      
      console.log("Saving group with data:", currentGroup);
      
      let response;
      if (!currentGroup.id) {
        // Create new group
        response = await axios.post('/api/products/groups', {
          name: currentGroup.name,
          description: currentGroup.description,
          sort_order: currentGroup.sort_order
        });
        
        console.log("Create group response:", response.data);
        
        if (!response.data || !response.data.id) {
          throw new Error("Failed to create group: Invalid response data");
        }
        
        toast({
          title: "Group created",
          description: `${currentGroup.name} has been created successfully.`,
        });
      } else {
        // Update existing group
        response = await axios.put(`/api/products/groups/${currentGroup.id}`, {
          name: currentGroup.name,
          description: currentGroup.description,
          sort_order: currentGroup.sort_order
        });
        
        console.log("Update group response:", response.data);
        
        if (!response.data || !response.data.id) {
          throw new Error("Failed to update group: Invalid response data");
        }
        
        toast({
          title: "Group updated",
          description: `${currentGroup.name} has been updated successfully.`,
        });
      }
      
      // Make sure to await fetchGroups to refresh the UI
      await fetchGroups();
      setIsGroupDialogOpen(false);
    } catch (error) {
      console.error('Error saving group:', error);
      toast({
        title: "Error saving group",
        description: error instanceof Error ? error.message : "There was a problem saving the group data.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!currentGroup) return;
    
    try {
      setIsSubmitting(true);
      await axios.delete(`/api/products/groups/${currentGroup.id}`);
      toast({
        title: "Group deleted",
        description: `${currentGroup.name} has been deleted.`,
      });
      // Await fetch groups to ensure UI updates
      await fetchGroups();
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting group:', error);
      toast({
        title: "Error deleting group",
        description: "There was a problem deleting the group.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteDialog = (group: Group) => {
    setCurrentGroup(group);
    setIsDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Group Management</h1>
        <Button onClick={() => handleOpenGroupDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Group
        </Button>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-md shadow">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Sort Order</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  No groups found.
                </TableCell>
              </TableRow>
            ) : (
              groups.map((group) => (
                <TableRow key={group.id}>
                  <TableCell className="font-medium">{group.name}</TableCell>
                  <TableCell>{group.description || ''}</TableCell>
                  <TableCell>{group.sort_order}</TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleOpenGroupDialog(group)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => openDeleteDialog(group)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <GroupDialog
        isOpen={isGroupDialogOpen}
        setIsOpen={setIsGroupDialogOpen}
        currentGroup={currentGroup}
        setCurrentGroup={setCurrentGroup}
        onSave={handleSaveGroup}
        isSubmitting={isSubmitting}
      />

      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${currentGroup?.name}"? This may affect products assigned to this group.`}
        onDelete={handleDelete}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default GroupsTab;
